import React, { useCallback, useContext, useEffect, useMemo, useState } from "react";
import { firestore } from "../firebase/firebase";
import { useAuth } from "./AuthContext";
import _ from "lodash";

const HRSettingsContext = React.createContext();

export function useHRSettings() {
  return useContext(HRSettingsContext);
}

export function HRSettingsProvider({ children }) {
  const [settings, setSettings] = useState({});
  const [types, setTypes] = useState([]); // Store types here
  const [subtypes, setSubtypes] = useState([]); // Store subtypes here
  const [loading, setLoading] = useState(true);
  const [hrLoading, setHrLoading] = useState(true);

  const { currentUser } = useAuth();

  useEffect(() => {
    // Only subscribe to Firestore when the user is authenticated
    if (!currentUser) {
      setLoading(false);
      setHrLoading(false);
      return;
    }

    setLoading(true);
    setHrLoading(true);

    // Set up Firestore real-time listener
    const unsubscribe = firestore.collection("humanResourcesSettings").onSnapshot(
      (querySnapshot) => {
        const response = querySnapshot.docs.map((doc) => ({
          ...doc.data(),
          docId: doc.id,
        }));

        // Initialize documents object to track index of "settings"
        let documents = {
          settings: "",
        };

        // Find the index of the 'settings' document and set the settings
        for (let i = 0; i < _.size(response); i++) {
          for (const [key] of Object.entries(documents)) {
            if (response[i].docId.toString() === [key].toString()) {
              documents = { ...documents, [key]: i };
            }
          }
        }

        // Set the settings data
        const settingsData = response[0];
        setSettings(settingsData);

        // Also set types and subtypes
        setTypes(settingsData?.types || []);
        setSubtypes(settingsData?.subtypes || []);

        setLoading(false);
        setHrLoading(false);
      },
      (error) => {
        console.warn("Error fetching data: ", error);
        setLoading(false);
        setHrLoading(false);
      }
    );

    // Clean up the listener on component unmount or when user changes
    return () => unsubscribe();
  }, [currentUser]);

  // Helper to save settings to Firestore
  const saveSettings = useCallback(async (updatedData) => {
    const docId = settings?.docId;
    if (!docId) {
      await firestore.collection("humanResourcesSettings").add(updatedData);
    } else {
      await firestore.collection("humanResourcesSettings").doc(docId).update(updatedData);
    }
    // onSnapshot will automatically update local state
  }, [settings]);

  // Add a new type (category)
  const addType = useCallback(async (name) => {
    const value = name.toLowerCase().replace(/ /g, "");
    const currentTypes = settings?.types || [];
    // Check for duplicate value
    if (currentTypes.some((t) => t.value === value)) {
      throw new Error("A folder with that name already exists.");
    }
    const newType = { name, value };
    const updatedTypes = [...currentTypes, newType];
    await saveSettings({ types: updatedTypes });
  }, [settings, saveSettings]);

  // Delete a type if it has no subtypes
  const deleteType = useCallback(async (typeValue) => {
    const currentSubtypes = settings?.subtypes || [];
    if (currentSubtypes.some((s) => s.parentValue === typeValue)) {
      throw new Error("Cannot delete this folder because it has folders inside it.");
    }
    const updatedTypes = (settings?.types || []).filter((t) => t.value !== typeValue);
    await saveSettings({ types: updatedTypes });
  }, [settings, saveSettings]);

  // Add a new subtype under a parent type, optionally nested under a parent folder
  const addSubtype = useCallback(async (parentValue, name, parentFolderValue) => {
    const value = name.toLowerCase().replace(/ /g, "");
    const currentSubtypes = settings?.subtypes || [];
    // Check for duplicate value within same type (values must be unique for routing)
    if (currentSubtypes.some((s) => s.parentValue === parentValue && s.value === value)) {
      throw new Error("A folder with that name already exists here.");
    }
    // Count siblings at the same level for order
    const siblings = parentFolderValue
      ? currentSubtypes.filter((s) => s.parentValue === parentValue && s.parentFolder === parentFolderValue)
      : currentSubtypes.filter((s) => s.parentValue === parentValue && !s.hasParentFolder);
    const order = siblings.length;
    const newSubtype = {
      name,
      value,
      hidden: false,
      override: false,
      overrideValue: "",
      appValue: "",
      hasParentFolder: !!parentFolderValue,
      hasSubfolders: false,
      parentValue,
      parentFolder: parentFolderValue || "",
      order,
    };
    const updatedSubtypes = [...currentSubtypes, newSubtype];
    await saveSettings({ subtypes: updatedSubtypes });
  }, [settings, saveSettings]);

  // Delete a subtype if no HR documents reference it and no child subtypes exist
  const deleteSubtype = useCallback(async (parentValue, subtypeValue) => {
    // Check if any child subtypes use this as their parent folder
    const currentSubtypesCheck = settings?.subtypes || [];
    if (currentSubtypesCheck.some((s) => s.parentFolder === subtypeValue && s.parentValue === parentValue)) {
      throw new Error("Cannot delete this folder because it has child folders.");
    }
    // Check if any HR documents use this subtype
    const docsSnapshot = await firestore
      .collection("humanResources")
      .where("hrType", "==", parentValue)
      .where("docType", "==", subtypeValue)
      .limit(1)
      .get();
    if (!docsSnapshot.empty) {
      throw new Error("Cannot delete this folder because it has documents.");
    }
    const currentSubtypes = settings?.subtypes || [];
    const deletedSubtype = currentSubtypes.find(
      (s) => s.parentValue === parentValue && s.value === subtypeValue
    );
    let updatedSubtypes = currentSubtypes.filter(
      (s) => !(s.parentValue === parentValue && s.value === subtypeValue)
    );
    // Reorder remaining subtypes within same parent
    if (deletedSubtype) {
      updatedSubtypes = updatedSubtypes.map((s) => {
        if (s.parentValue === parentValue && s.order > deletedSubtype.order) {
          return { ...s, order: s.order - 1 };
        }
        return s;
      });
    }
    await saveSettings({ subtypes: updatedSubtypes });
  }, [settings, saveSettings]);

  // Update a type's name
  const updateType = useCallback(async (typeValue, updates) => {
    const updatedTypes = (settings?.types || []).map((t) => {
      if (t.value === typeValue) {
        return { ...t, ...updates };
      }
      return t;
    });
    await saveSettings({ types: updatedTypes });
  }, [settings, saveSettings]);

  // Update a subtype's fields (name, hidden, hasSubfolders, hasParentFolder, parentFolder)
  const updateSubtype = useCallback(async (parentValue, subtypeValue, updates) => {
    const updatedSubtypes = (settings?.subtypes || []).map((s) => {
      if (s.parentValue === parentValue && s.value === subtypeValue) {
        return { ...s, ...updates };
      }
      return s;
    });
    await saveSettings({ subtypes: updatedSubtypes });
  }, [settings, saveSettings]);

  // Reorder types (array order determines display order)
  const reorderTypes = useCallback(async (orderedValues) => {
    const currentTypes = settings?.types || [];
    const reordered = orderedValues
      .map((val) => currentTypes.find((t) => t.value === val))
      .filter(Boolean);
    await saveSettings({ types: reordered });
  }, [settings, saveSettings]);

  // Add a new subtype with subfolders in a single save
  const addSubtypeWithSubfolders = useCallback(async (parentValue, name, subfolderNames, parentFolderValue) => {
    const value = name.toLowerCase().replace(/ /g, "");
    const currentSubtypes = settings?.subtypes || [];

    // Validate no duplicate for the parent
    if (currentSubtypes.some((s) => s.parentValue === parentValue && s.value === value)) {
      throw new Error("A folder with that name already exists here.");
    }

    // Validate no duplicate subfolder values among themselves or with existing subtypes
    const subfolderValues = subfolderNames.map((n) => n.toLowerCase().replace(/ /g, ""));
    const uniqueCheck = new Set(subfolderValues);
    if (uniqueCheck.size !== subfolderValues.length) {
      throw new Error("Subfolder names must be unique.");
    }
    for (const sv of subfolderValues) {
      if (currentSubtypes.some((s) => s.parentValue === parentValue && s.value === sv)) {
        throw new Error(`A folder with the value "${sv}" already exists here.`);
      }
    }

    // Count siblings for order
    const siblings = parentFolderValue
      ? currentSubtypes.filter((s) => s.parentValue === parentValue && s.parentFolder === parentFolderValue)
      : currentSubtypes.filter((s) => s.parentValue === parentValue && !s.hasParentFolder);
    const parentOrder = siblings.length;

    // Create parent subtype with hasSubfolders: true
    const parentSubtype = {
      name,
      value,
      hidden: false,
      override: false,
      overrideValue: "",
      appValue: "",
      hasParentFolder: !!parentFolderValue,
      hasSubfolders: true,
      parentValue,
      parentFolder: parentFolderValue || "",
      order: parentOrder,
    };

    // Create child subtypes
    const childSubtypes = subfolderNames.map((childName, index) => ({
      name: childName,
      value: childName.toLowerCase().replace(/ /g, ""),
      hidden: false,
      override: false,
      overrideValue: "",
      appValue: "",
      hasParentFolder: true,
      hasSubfolders: false,
      parentValue,
      parentFolder: value,
      order: index,
    }));

    const updatedSubtypes = [...currentSubtypes, parentSubtype, ...childSubtypes];
    await saveSettings({ subtypes: updatedSubtypes });
  }, [settings, saveSettings]);

  // Create a subfolder under an existing subtype (enabling hasSubfolders on parent) in one atomic save
  const createSubfolderUnder = useCallback(async (parentValue, parentSubtypeValue, name) => {
    const value = name.toLowerCase().replace(/ /g, "");
    const currentSubtypes = settings?.subtypes || [];

    if (currentSubtypes.some((s) => s.parentValue === parentValue && s.value === value)) {
      throw new Error("A folder with that name already exists here.");
    }

    // Set hasSubfolders on the parent + keep everything else
    const updatedSubtypes = currentSubtypes.map((s) => {
      if (s.parentValue === parentValue && s.value === parentSubtypeValue) {
        return { ...s, hasSubfolders: true };
      }
      return s;
    });

    // Count existing siblings under this parent folder
    const siblings = updatedSubtypes.filter(
      (s) => s.parentValue === parentValue && s.parentFolder === parentSubtypeValue
    );

    const newSubtype = {
      name,
      value,
      hidden: false,
      override: false,
      overrideValue: "",
      appValue: "",
      hasParentFolder: true,
      hasSubfolders: false,
      parentValue,
      parentFolder: parentSubtypeValue,
      order: siblings.length,
    };

    await saveSettings({ subtypes: [...updatedSubtypes, newSubtype] });
    return value;
  }, [settings, saveSettings]);

  // Move a batch of HR documents to a new folder
  const moveFormsBatch = useCallback(async (docIds, newHrType, newDocType) => {
    const batch = firestore.batch();
    docIds.forEach((docId) => {
      const ref = firestore.collection("humanResources").doc(docId);
      batch.update(ref, { hrType: newHrType, docType: newDocType });
    });
    await batch.commit();
  }, []);

  // Reorder subtypes within a specific parent context
  const reorderSubtypes = useCallback(async (parentValue, parentFolderValue, orderedValues) => {
    const currentSubtypes = settings?.subtypes || [];
    const updatedSubtypes = currentSubtypes.map((s) => {
      // Only update order for subtypes in the same parent context
      const isInContext = parentFolderValue
        ? s.parentValue === parentValue && s.parentFolder === parentFolderValue
        : s.parentValue === parentValue && !s.hasParentFolder;
      if (isInContext) {
        const newOrder = orderedValues.indexOf(s.value);
        if (newOrder !== -1) {
          return { ...s, order: newOrder };
        }
      }
      return s;
    });
    await saveSettings({ subtypes: updatedSubtypes });
  }, [settings, saveSettings]);

  const value = useMemo(() => ({
    setSettings,
    settings,
    types,
    subtypes,
    loading,
    hrLoading,
    addType,
    deleteType,
    addSubtype,
    addSubtypeWithSubfolders,
    deleteSubtype,
    createSubfolderUnder,
    moveFormsBatch,
    updateType,
    updateSubtype,
    reorderTypes,
    reorderSubtypes,
  }), [setSettings, settings, types, subtypes, loading, hrLoading, addType, deleteType, addSubtype, addSubtypeWithSubfolders, deleteSubtype, createSubfolderUnder, moveFormsBatch, updateType, updateSubtype, reorderTypes, reorderSubtypes]);

  return (
    <HRSettingsContext.Provider value={value}>
      {children}
    </HRSettingsContext.Provider>
  );
}
